export const DRAFT_PROPOSAL_FLOW = `
graph LR
    Start((Start)) --> Phase1_Requirements
    End((End))
    Fail((Failed))

    subgraph Phase1_Requirements ["Phase 1: Requirements Extraction"]
        direction TB
        Prepare_Proposal_Extract["<b>Prepare Proposal Extract</b><br/>Scans available tender docs and splits<br/>them into batches for fast extraction."] --> Map_Extract_Requirements["<b>Map: Extract Requirements (Max 15)</b><br/>Concurrently processes document batches<br/>to extract criteria and constraints."]
        Map_Extract_Requirements --> Consolidate_Requirements["<b>Consolidate Requirements</b><br/>Merges parallel extraction results into<br/>a single comprehensive list."]
        Consolidate_Requirements --> Wait_For_Phase1_Review["<b>Wait For Phase 1 Review</b><br/>Pauses workflow for external approval<br/>of the consolidated requirements."]
    end

    Phase1_Requirements --> Phase2_Drafting

    subgraph Phase2_Drafting ["Phase 2: Strategy & Drafting"]
        direction TB
        Generate_Win_Strategy["<b>Generate Win Strategy</b><br/>LLM creates a core strategic brief to<br/>guide tone and selling points."] --> Prepare_Drafting_Batches["<b>Prepare Drafting Batches</b><br/>Breaks down required proposal sections<br/>into discrete chunks for drafting."]
        Prepare_Drafting_Batches --> Map_Draft_Content["<b>Map: Draft Content (Max 5)</b><br/>Concurrently generates text for specific<br/>sections guided by the Win Strategy."]
        Map_Draft_Content --> Coherence_Review["<b>Coherence Review</b><br/>Reviews all parallel drafts together to<br/>ensure consistency and unified tone."]
        Coherence_Review --> Wait_For_Phase2_Review["<b>Wait For Phase 2 Review</b><br/>Pauses workflow for approval of<br/>the coherent drafted text."]
    end

    Phase2_Drafting --> Phase3_Narrative

    subgraph Phase3_Narrative ["Phase 3: Team, References & Narrative"]
        direction TB
        Select_Team_And_References{"Parallel Selection"}
        Select_Team_And_References --> Select_Team["<b>Select Team</b><br/>LLM selects best-fit personnel."]
        Select_Team_And_References --> Select_References["<b>Select References</b><br/>LLM selects relevant past projects."]
        Select_Team --> Generate_Narrative_Sections["<b>Generate Narrative Sections</b><br/>Writes 5 focused narratives weaving<br/>selected team and references together."]
        Select_References --> Generate_Narrative_Sections
        Generate_Narrative_Sections --> Wait_For_Phase3_Review["<b>Wait For Phase 3 Review</b><br/>Final pause for approval of<br/>generated narrative sections."]
    end

    Phase3_Narrative --> Assemble_Document["<b>Assemble Document</b><br/>Stitches all approved sections, profiles,<br/>and narratives into final proposal format."]
    Assemble_Document --> End

    Mark_As_Failed["<b>Mark As Failed</b><br/>Logs error and reports failure."] --> Fail_Workflow["Fail Workflow"]
    Fail_Workflow --> Fail

    Phase1_Requirements -.-> |Error| Mark_As_Failed
    Phase2_Drafting -.-> |Error| Mark_As_Failed
    Phase3_Narrative -.-> |Error| Mark_As_Failed
    Assemble_Document -.-> |Error| Mark_As_Failed
`;

export const REVIEW_PROPOSAL_FLOW = `
graph LR
    Start((Start)) --> Phase1_Baseline
    End((End))
    Fail((Failed))

    subgraph Phase1_Baseline ["Phase 1: Baseline Requirements"]
        direction TB
        Prepare_Extract_Batches["<b>Prepare Extract Batches</b><br/>Lists all relevant Aurora project docs<br/>and prepares parallel inputs."] --> Map_Extract_Requirements["<b>Map: Extract Requirements (Max 15)</b><br/>Concurrently extracts rules and constraints<br/>from source documents."]
        Map_Extract_Requirements --> Consolidate_Requirements["<b>Consolidate Results</b><br/>Merges extraction data into a master<br/>list of compliance criteria."]
        Consolidate_Requirements --> Wait_For_Review_Phase1["<b>Wait For Phase 1 Review</b><br/>Pauses for human/system validation<br/>of extracted criteria."]
    end

    Phase1_Baseline --> Phase2_Compliance

    subgraph Phase2_Compliance ["Phase 2: Compliance Check"]
        direction TB
        Prepare_Compliance_Batches["<b>Prepare Compliance Batches</b><br/>Splits Phase 1 items into smaller<br/>chunks of 5 for checking."] --> Map_Compliance_Batches["<b>Map: Compliance Checks (Max 10)</b><br/>Concurrently checks drafted document<br/>against compliance chunks."]
        Map_Compliance_Batches --> Wait_For_Review_Phase2["<b>Wait For Phase 2 Review</b><br/>Pauses for validation of basic<br/>compliance check results."]
    end

    Phase2_Compliance --> Phase3_DeepAnalysis

    subgraph Phase3_DeepAnalysis ["Phase 3: Intensive Parallel Audits"]
        direction TB
        Branch{"Concurrent Analysis"}
        Branch --> Team_Audit["<b>Team Audit</b><br/>Checks if proposed team<br/>meets all qualifications."]
        Branch --> Reference_Audit["<b>Reference Audit</b><br/>Verifies selected references<br/>map to required scope."]
        Branch --> Scoring_Alignment["<b>Scoring Alignment</b><br/>Analyzes draft against<br/>evaluation/scoring matrix."]
        Branch --> Coherence_Review["<b>Coherence Review</b><br/>Secondary pass for perfect<br/>logic and narrative flow."]
        Branch --> Strategic_Blindspots["<b>Strategic Blindspots</b><br/>Actively looks for weaknesses,<br/>missing arguments, or risks."]
        Team_Audit --> Wait_For_Review_Phase3["<b>Wait For Phase 3 Review</b><br/>Pauses for acknowledgement of<br/>deep analysis failures/blindspots."]
        Reference_Audit --> Wait_For_Review_Phase3
        Scoring_Alignment --> Wait_For_Review_Phase3
        Coherence_Review --> Wait_For_Review_Phase3
        Strategic_Blindspots --> Wait_For_Review_Phase3
    end

    Phase3_DeepAnalysis --> Phase4_Fixes

    subgraph Phase4_Fixes ["Phase 4: Fix Generation"]
        direction TB
        Generate_Review_Report["<b>Generate Review Report</b><br/>Scans identified failures & blindspots<br/>and groups them into fix batches."] --> Map_Generate_Fixes["<b>Map: Generate Fixes (Max 5)</b><br/>Concurrently processes batches to suggest<br/>fixes or rewrites for the failures."]
        Map_Generate_Fixes --> Finalize_Review["<b>Finalize Review</b><br/>Compiles audits, checks, and generated<br/>fixes into a final review output."]
    end

    Phase4_Fixes --> End

    Mark_As_Failed["<b>Mark As Failed</b><br/>Logs error and reports failure."] --> Fail_Workflow["Fail Workflow"]
    Fail_Workflow --> Fail

    Phase1_Baseline -.-> |Error| Mark_As_Failed
    Phase2_Compliance -.-> |Error| Mark_As_Failed
    Phase3_DeepAnalysis -.-> |Error| Mark_As_Failed
    Phase4_Fixes -.-> |Error| Mark_As_Failed
`;

export const COMPANY_ONBOARDING_FLOW = `
graph LR
    Start((Start)) --> Phase_Intake
    End((End))
    Fail((Failed))

    subgraph Phase_Intake ["Phase: Document Intake & Extraction"]
        direction TB
        Upload_And_Classify["<b>Upload & Classify</b><br/>User uploads documents. AI classifies<br/>each file as CV, Project, or Company."] --> Prepare_Extract_Batches_OB["<b>Prepare Extract Batches</b><br/>Groups classified documents into<br/>processing batches by type."]
        Prepare_Extract_Batches_OB --> Map_Extract_Data["<b>Map: Extract Data (Max 10)</b><br/>Concurrently extracts structured data<br/>from each document using LLM."]
    end

    Phase_Intake --> Phase_Workforce

    subgraph Phase_Workforce ["Phase 1: Workforce"]
        direction TB
        Diff_Workforce["<b>Diff Workforce</b><br/>Compares extracted people against<br/>existing workforce (fuzzy name match)."] --> Wait_Phase1["<b>Wait For Phase 1 Review</b><br/>Pauses for user to review<br/>workforce additions and updates."]
        Wait_Phase1 --> Apply_Workforce["<b>Apply Workforce Changes</b><br/>Writes approved workforce additions<br/>and modifications to DynamoDB."]
    end

    Phase_Workforce --> Phase_Projects

    subgraph Phase_Projects ["Phase 2: Projects"]
        direction TB
        Diff_Projects["<b>Diff Projects</b><br/>Compares extracted projects against<br/>existing company projects."] --> Wait_Phase2["<b>Wait For Phase 2 Review</b><br/>Pauses for user to review<br/>project additions and updates."]
        Wait_Phase2 --> Apply_Projects["<b>Apply Project Changes</b><br/>Writes approved project additions<br/>and modifications to DynamoDB."]
    end

    Phase_Projects --> Phase_Profile

    subgraph Phase_Profile ["Phase 3: Profile"]
        direction TB
        Enrich_Profile["<b>Enrich Company Profile</b><br/>LLM generates or updates company<br/>profile sections from documents."] --> Wait_Phase3["<b>Wait For Phase 3 Review</b><br/>Pauses for user to review<br/>company profile changes."]
        Wait_Phase3 --> Apply_Profile["<b>Apply Profile Changes</b><br/>Writes approved profile sections<br/>to company DynamoDB table."]
    end

    Phase_Profile --> Finalize_Onboarding["<b>Finalize Onboarding</b><br/>Marks onboarding as complete<br/>and cleans up temp data."]
    Finalize_Onboarding --> End

    Mark_As_Failed_OB["<b>Mark As Failed</b><br/>Logs error and reports failure."] --> Fail_Workflow_OB["Fail Workflow"]
    Fail_Workflow_OB --> Fail

    Phase_Intake -.-> |Error| Mark_As_Failed_OB
    Phase_Workforce -.-> |Error| Mark_As_Failed_OB
    Phase_Projects -.-> |Error| Mark_As_Failed_OB
    Phase_Profile -.-> |Error| Mark_As_Failed_OB
`;

export const CHAT_FLOW = `
graph TD
    Start((Start)) --> User_Question
    End((End))
    Fail((Failed))

    User_Question["<b>User Question</b><br/>Prompt, selected docs,<br/>optional lot + history."] --> Fetch_Tender_Docs

    Fetch_Tender_Docs["<b>Fetch Tender Doc Metadata</b><br/>Reads qabot-TenderDocuments<br/>(DynamoDB) for summaries<br/>and S3 paths."] --> Filter_Phase

    subgraph Filter_Phase ["Phase: LLM Filtering"]
        direction LR
        Filter_Docs["<b>LLM: Filter Documents</b><br/>Short-summary pass picks<br/>which selected docs are<br/>actually relevant."]
        Filter_Docs --> Classify_Company["<b>LLM: Classify Company Need</b><br/>Picks category A/B/C/D to<br/>decide which company context<br/>to inject (none, workforce,<br/>profile, or all)."]
    end

    Filter_Phase --> Context_Phase

    subgraph Context_Phase ["Phase: Context Assembly"]
        direction LR
        Fetch_Docs["<b>Fetch Document Content</b><br/>Aurora: project_document_v<br/>and project_document_qa_v<br/>for the chosen docs."]
        Fetch_Docs --> Fetch_Company["<b>Fetch Company Data</b><br/>DynamoDB: profile, workforce,<br/>past projects + selected<br/>company documents."]
        Fetch_Company --> Assemble_Context["<b>Assemble Full Context</b><br/>Tender docs + Q&A +<br/>company docs + filtered<br/>company data."]
    end

    Context_Phase --> Generate_Answer

    Generate_Answer["<b>LLM: Generate Answer</b><br/>Gemini answers using persona,<br/>chat history and the<br/>assembled context."] --> Build_References

    Build_References["<b>Build References Map</b><br/>Parse [file.pdf] citations,<br/>resolve to real S3 keys for<br/>frontend click-through."] --> Return_Response

    Return_Response["<b>Return {answer, references}</b><br/>Rendered by the chat UI<br/>with inline source links."] --> End

    Mark_As_Failed_Chat["<b>Mark As Failed</b><br/>HTTP 500 + CloudWatch log.<br/>Chat UI shows error banner."] --> Fail

    Fetch_Tender_Docs -.-> |Error| Mark_As_Failed_Chat
    Filter_Docs -.-> |Error| Mark_As_Failed_Chat
    Fetch_Docs -.-> |Error| Mark_As_Failed_Chat
    Generate_Answer -.-> |Error| Mark_As_Failed_Chat
`;
